/**
 * NE4 (.334): der Tagesplan der Teams, mit Fertigmeldung am Handy.
 *
 * ORIGINALTEXT MARVIN (08.10.2026)
 *   „Bei deren Beauftragung handelt es sich immer um ein Team mit zwei
 *    Personen. Und es gibt einen Teamzugang, keinen Personenzugang."
 *   „der Nachunternehmer sieht ja nur seine Termine, die gebucht wurden und
 *    nicht die gesamte Liste des Eingangs."
 *
 * Ein Teamzugang sieht seinen Tag; ein Firmenzugang die Tage seiner Teams;
 * die Dispo alle. Die Grenze zieht der Server, hier wird gezeigt, was kommt.
 */
import { useEffect, useState } from 'react';
import { offenAusAdresse, offenInAdresse } from '../teile/adresse.ts';
import { api, darf, type Anmelder } from '../api.ts';
import { Kopf, Karte } from '../teile/Geruest.tsx';
import { Dialog, Fehlerkasten, Text, ausFehler, type Fehlerlage } from '../teile/Formular.tsx';
import { Suchfeld, passtSuche } from '../teile/Schluesselsuche.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import { AuftragsKarte, tagLang } from './Ne4Arbeit.tsx';
import { FirmaHaus } from './Ne4Firma.tsx';
import { TerminMarke } from '../teile/Ne4Bausteine.tsx';
import { heuteTag, kurzTag, kwVon, montagVon as montagTag, plusTage, wochentagKurz, jahrVon } from '../teile/rastertage.ts';

const GRUENDE = ['Kunde nicht angetroffen', 'Kein Zugang zum Hausanschluss', 'Technik: Anschluss nicht möglich', 'Kunde hat abgesagt', 'Anderes'];

export function Ne4Tagesplan({ ich, start = 'tag' }: { ich: Anmelder; start?: 'tag' | 'woche' }) {
  const heute = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
  const [tag, setTag] = useState(heute);
  const [d, setD] = useState<any>(null);
  const [kommende, setKommende] = useState<any[]>([]);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [melden, setMelden] = useState<{ termin: any; art: 'fertig' | 'nicht' | 'verzoegerung' } | null>(null);
  /* .595: die Baustelle öffnet sich aus Tages- und Wochenplan (Firma: ihr Haus, Dispo: die Auftragskarte). */
  const seite = start === 'woche' ? 'ne4_wochenplan' : 'ne4_tagesplan';
  const [haus, setHaus] = useState<string | null>(() => offenAusAdresse(seite));
  useEffect(() => { offenInAdresse(seite, haus); }, [haus]);
  /* .559: DER WOCHENPLAN DER KOLONNEN. Marvin, 30.10.2026: „Ich brauche einen
     Wochenplan für die Kolonnen, beziehungsweise haben wir den Tagesplan. Aber
     wir brauchen dann einen Wochenplan für die einzelnen Teams und Kolonnen". */
  const [ansicht, setAnsicht] = useState<'tag' | 'woche'>(start);
  /* .590: der Wochenplan hat seinen eigenen Menüpunkt (Marvin: „denen fehlt sozusagen dort der Wochenplan“). */
  useEffect(() => { setAnsicht(start); }, [start]);
  const laden = () => Promise.all([api.ne4Tagesplan(tag).then(setD), api.ne4MeineTermine().then(setKommende).catch(() => setKommende([]))]).catch((f) => setFehler(ausFehler(f)));
  useEffect(() => { laden(); }, [tag]);
  useFrisch(laden);
  const kannMelden = darf(ich, 'ne4.tagesplan.melden');
  /* .593: Tag für Tag, auch das Wochenende (es lässt sich wieder buchen). */
  const schiebenWerktag = (n: number) => setTag(plusTage(tag, n));
  const navi = (t: any) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${t.strasse ?? ''} ${t.hausnummer ?? ''}, ${t.plz ?? ''} ${t.ort ?? ''}`)}`;
  const firmaSicht = !darf(ich, 'ne4.auftraege.lesen');
  if (haus && firmaSicht && darf(ich, 'ne4.firmenauftraege.lesen')) return <FirmaHaus ich={ich} id={haus} zu={() => { setHaus(null); void laden(); }} />;
  const hausFenster = haus && !firmaSicht ? <AuftragsKarte ich={ich} id={haus} zu={() => setHaus(null)} geaendert={laden} /> : null;
  if (ansicht === 'woche') return <><Wochenplan zumTag={(t) => { setTag(t); setAnsicht('tag'); }} zurTagesansicht={() => setAnsicht('tag')} oeffne={setHaus} />{hausFenster}</>;
  return (
    <>
      {ansicht === 'tag' && <Kopf titel="NE4, Tagesplan" weg={tagLang(tag)} rechts={
        <div className="reihe">
          <span className="reihe" style={{ gap: 0 }}>
            <button type="button" className="knopf klein haupt" onClick={() => setAnsicht('tag')}>Tag</button>
            <button type="button" className="knopf klein" onClick={() => setAnsicht('woche')}>Woche</button>
          </span>
          <button type="button" className="knopf klein" onClick={() => schiebenWerktag(-1)}>◀</button>
          <input type="date" value={tag} onChange={(e) => e.target.value && setTag(e.target.value)} />
          <button type="button" className="knopf klein" onClick={() => schiebenWerktag(1)}>▶</button>
        </div>} />}
      <div className="inhalt">
      <Fehlerkasten lage={fehler} />
      {ansicht === 'tag' && <>
      {/* .595: DER TAG SIEHT AUS WIE EINE WOCHE DES WOCHENPLANS. Marvin, 02.11.2026: „der NE4-Tagesplan sieht
          völlig anders aus wie der Wochenplan ... Wenn die hier auf dem Kalender sind, Tagesplan, Wochenplan,
          dann soll aber auch die Baustelle anklickbar sein, dass sie sich öffnet." Ein Block mit KW und Tag,
          je Kolonne eine Zeile mit ihren Leuten, rechts die Baustellen des Tages. */}
      <div className="wochenblock">
        <div className="wochenkopf">
          <span className="kwmarke">KW {kwVon(tag)}</span>
          <b>{tagLang(tag)}</b>
          <div className="wachsen" />
          <span className="klein leise">{(d?.teamtage ?? []).reduce((n: number, tt: any) => n + tt.termine.length, 0)} Termine</span>
        </div>
        {d && !d.teamtage.length && <div className="leise" style={{ padding: 12 }}>An diesem Tag hat {d.mein_team ? 'dein Team' : 'keine Kolonne'} etwas geplant.</div>}
        {!!d?.teamtage?.length && (
          <table className="ne4-woche">
            <thead><tr><th style={{ width: 180 }}>Kolonne</th><th>Baustellen</th></tr></thead>
            <tbody>{d.teamtage.map((tt: any) => (
              <tr key={tt.id}>
                <td><b>{tt.team}</b>{!d.mein_team && <div className="klein leise">{tt.firma}</div>}
                  {String(tt.personen ?? '').split(',').map((x: string) => x.trim()).filter(Boolean).map((x: string) => <div key={x} className="klein">{x}</div>)}</td>
                <td className={tt.gesperrt ? 'gesperrt' : tt.termine.length ? 'belegt' : 'frei'}>
                  {tt.gesperrt && <div className="klein">gesperrt{tt.grund ? `: ${tt.grund}` : ''}</div>}
                  {!tt.termine.length && !tt.gesperrt && <span className="klein leise">frei</span>}
                  {tt.termine.map((t: any) => (
                    <div key={t.id} className="ne4-tagkarte">
                      <div className="reihe" style={{ justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
                        <div style={{ minWidth: 260 }}>
                          <button type="button" className="ne4-baustelle alslink" style={{ textAlign: 'left' }} onClick={() => setHaus(t.auftrag_id)} title="Baustelle öffnen">
                            <b style={{ fontSize: 17 }}>{t.strasse} {t.hausnummer}</b> <span>{t.plz} {t.ort}</span>
                          </button>
                          <div className="ne4-terminreihe" style={{ margin: '6px 0' }}><TerminMarke t={{ ...t, tag }} /></div>
                          <div className="klein"><b>{t.eigentuemer_org || [t.vorname, t.name].filter(Boolean).join(' ')}</b>{t.eigentuemer_org && [t.vorname, t.name].filter(Boolean).length ? `, ${[t.vorname, t.name].filter(Boolean).join(' ')}` : ''}</div>
                          {t.art === 'GEB' && <div className="klein">{t.etagen ?? '–'} Etagen, {t.wohneinheiten ?? '–'} WE, {t.ge ?? '–'} GE, {t.sl ?? '–'} SL{!!t.wohnungen?.length && <>, <b>{t.wohnungen.filter((w: any) => w.erledigt_am).length} von {t.wohnungen.length} Connects fertig</b></>}</div>}
                          <div className="reihe" style={{ gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                            {t.mobil && <a className="knopf klein" href={`tel:${String(t.mobil).replace(/\s+/g, '')}`}>📞 {t.mobil}</a>}
                            {t.telefon && <a className="knopf klein" href={`tel:${String(t.telefon).replace(/\s+/g, '')}`}>📞 {t.telefon}</a>}
                            <a className="knopf klein" href={navi(t)} target="_blank" rel="noreferrer">Navigation</a>
                          </div>
                          {t.notiz && <div className="klein leise">Notiz: {t.notiz}</div>}
                          {t.zustand === 'ausgefuehrt' && <div className="klein" style={{ color: 'var(--erledigt, #2a7)' }}>Fertig gemeldet{t.bericht ? `: ${t.bericht}` : ''}</div>}
                          {t.zustand === 'nicht_erledigt' && <div className="klein" style={{ color: 'var(--rot, #a33)' }}>Nicht erledigt: {t.grund}</div>}
                        </div>
                        {kannMelden && t.zustand === 'gebucht' && (
                          <div className="reihe" style={{ flexDirection: 'column', gap: 6 }}>
                            {!t.begonnen_am && <button type="button" className="knopf haupt" disabled={tag > heute} title={tag > heute ? 'Geht ab dem Termintag' : ''} onClick={() => { api.ne4Begonnen(t.id).then(laden).catch((f) => setFehler(ausFehler(f))); }}>In Arbeit nehmen</button>}
                            <button type="button" className={'knopf' + (t.begonnen_am ? ' haupt' : '')} onClick={() => setMelden({ termin: t, art: 'fertig' })}>Fertig melden</button>
                            <button type="button" className="knopf" onClick={() => setMelden({ termin: t, art: 'nicht' })}>Nicht erledigt</button>
                            <button type="button" className="knopf" onClick={() => setMelden({ termin: t, art: 'verzoegerung' })}>Verzögerung melden</button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
      {!!kommende.length && (
        <Karte titel="Die nächsten Tage">
          <table className="liste">
            <thead><tr><th>Tag</th><th>Team</th><th>Gebiet</th><th className="zahl">Termine</th></tr></thead>
            <tbody>{kommende.filter((k) => k.tag >= heute).slice(0, 30).map((k, i) => (
              <tr key={i} onClick={() => setTag(k.tag)}><td>{tagLang(k.tag)}</td><td>{k.team} <span className="klein leise">{k.firma}</span></td><td className="klein">{k.gesperrt ? 'gesperrt' : (k.gebiet ?? '')}</td><td className="zahl">{k.termine}/{k.plaetze}</td></tr>))}</tbody>
          </table>
        </Karte>
      )}
      {melden && d && melden.art !== 'verzoegerung' && <MeldeDialog termin={melden.termin} art={melden.art} positionen={d.positionen} zu={() => setMelden(null)} fertig={() => { setMelden(null); laden(); }} />}
      {melden?.art === 'verzoegerung' && <VerzoegerungDialog termin={melden.termin} titel={`${melden.termin.strasse ?? ''} ${melden.termin.hausnummer ?? ''}`} zu={() => setMelden(null)} fertig={() => { setMelden(null); laden(); }} />}
      {hausFenster}
      </>}
      </div>
    </>
  );
}

/**
 * DER WOCHENPLAN DER KOLONNEN WIE IN NE3 (.592).
 *
 * ORIGINALTEXT MARVIN (02.11.2026, als Firma angemeldet)
 *   „Der Wochenplan muss die Wochen untereinander nach Kalenderwoche zeigen,
 *    wie bei NE3 ... Samstagsarbeit erstmal überall raus ... die Leute
 *    untereinander auflisten, wie bei NE3."
 *
 * Wie der NE3-Wochenplan (`Terminplaner`, `Wochenraster`): 12 Wochen
 * untereinander, je Woche ein Block mit der KW, die laufende zuerst; ← und →
 * blättern um die gezeigte Spanne, „Heute" springt zurück. In jedem Block je
 * Kolonne eine Zeile mit ihren Leuten untereinander, Montag bis Freitag. Rot
 * belegt, grün frei, grau gestreift gesperrt, Urlaub aus der Verfügbarkeit.
 */
const WOCHEN = 12;
function Wochenplan({ zumTag, zurTagesansicht, oeffne }: { zumTag: (t: string) => void; zurTagesansicht: () => void; oeffne: (id: string) => void }) {
  const [start, setStart] = useState(() => montagTag(heuteTag()));
  const ende = plusTage(start, WOCHEN * 7 - 1);
  const [b, setB] = useState<{ teams: any[]; tage: any[]; feiertage?: { tag: string; name: string }[] } | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const laden = () => api.ne4Belegung(start, ende).then(setB).catch((f) => setFehler(ausFehler(f)));
  useEffect(() => { void laden(); }, [start]); // eslint-disable-line react-hooks/exhaustive-deps
  useFrisch(laden);
  /* .590: FIRMA, TEAM UND SUCHE WIE IN NE3. */
  const [firma, setFirma] = useState('');
  const [team, setTeam] = useState('');
  const [suche, setSuche] = useState('');
  const heute = heuteTag();
  /* .593: SAMSTAG UND SONNTAG WIE IN NE3 nur, wenn dort etwas ist (Termin oder als verfügbar gemeldet).
     Marvin: „Wochenende muss ich noch buchen lassen, aber es ist nicht standardmäßig vorselektiert." */
  const hatWas = (t: string) => (b?.tage ?? []).some((z: any) => z.tag === t && ((z.termine ?? []).length || z.verfuegbarkeit === 'frei'));
  const wochen = Array.from({ length: WOCHEN }, (_, w) => Array.from({ length: 7 }, (_, i) => plusTage(start, w * 7 + i)).filter((t, i) => i < 5 || hatWas(t)));
  const feiertag = (t: string) => (b?.feiertage ?? []).find((f: any) => f.tag === t)?.name ?? null;
  const firmen = [...new Set<string>((b?.teams ?? []).map((t: any) => String(t.firma ?? '')).filter(Boolean))].sort((x, y) => x.localeCompare(y, 'de'));
  const q = suche.trim().toLowerCase();
  const passt = (te: any) => !q || q.replace(/[,;]/g, ' ').split(/\s+/).filter(Boolean).every((w) => passtSuche(w, te.kls_id, `${te.strasse ?? ''} ${te.hausnummer ?? ''}`, te.plz, te.ort, te.name));
  const teams = (b?.teams ?? []).filter((t: any) => (!firma || t.firma === firma) && (!team || t.id === team)
    && (!q || (b?.tage ?? []).some((z: any) => z.team_id === t.id && (z.termine ?? []).some(passt)) || `${t.name} ${t.personen ?? ''}`.toLowerCase().includes(q)));
  const leute = (tm: any): string[] => String(tm.personen ?? '').split(',').map((x) => x.trim()).filter(Boolean);
  /* Bei einer Suche nur die Wochen, in denen ein Treffer steht (wie NE3: der Plan zeigt den gesuchten Auftrag). */
  const sichtbar = wochen.filter((w) => !q || teams.some((tm: any) => w.some((t) => (b?.tage ?? []).some((z: any) => z.team_id === tm.id && z.tag === t && (z.termine ?? []).some(passt)))));
  return (
    <>
      <Kopf titel="NE4, Wochenplan" weg={`${tagLang(start)} bis ${tagLang(plusTage(ende, -2))}${firma ? `, ${firma}` : ''}`} rechts={
        <div className="reihe">
          <span className="reihe" style={{ gap: 0 }}>
            <button type="button" className="knopf klein" onClick={zurTagesansicht}>Tag</button>
            <button type="button" className="knopf klein haupt">Woche</button>
          </span>
          <button type="button" className="knopf" onClick={() => setStart(plusTage(start, -WOCHEN * 7))}>&larr;</button>
          <button type="button" className="knopf" onClick={() => setStart(montagTag(heuteTag()))}>Heute</button>
          <button type="button" className="knopf" onClick={() => setStart(plusTage(start, WOCHEN * 7))}>&rarr;</button>
        </div>} />
      <div className="inhalt">
        <Fehlerkasten lage={fehler} />
        <div className="reihe" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 10, alignItems: 'flex-end' }}>
          {firmen.length > 1 && (
            <div className="feld" style={{ width: 240, margin: 0 }}>
              <label>Firma</label>
              <select value={firma} onChange={(e) => { setFirma(e.target.value); setTeam(''); }}><option value="">alle Firmen</option>{firmen.map((f) => <option key={f} value={f}>{f}</option>)}</select>
            </div>
          )}
          <div className="feld" style={{ width: 240, margin: 0 }}>
            <label>Team</label>
            <select value={team} onChange={(e) => setTeam(e.target.value)}>
              <option value="">alle Teams{firma ? ` von ${firma}` : ''}</option>
              {(b?.teams ?? []).filter((t: any) => !firma || t.firma === firma).map((t: any) => <option key={t.id} value={t.id}>{t.name}{firma || firmen.length < 2 ? '' : ` (${t.firma})`}</option>)}
            </select>
          </div>
          <div className="feld" style={{ width: 280, margin: 0 }}>
            <label>Suche</label>
            <Suchfeld wert={suche} setze={setSuche} mehr="Name" breit={280} />
          </div>
          {(firma || team || suche) && <button type="button" className="knopf klein" onClick={() => { setFirma(''); setTeam(''); setSuche(''); }}>Zurücksetzen</button>}
        </div>
        {!b && <div className="klein leise">Wird geladen.</div>}
        {b && !b.teams.length && <div className="klein leise">Keine Kolonne angelegt (Benutzer und Rechte, Firmen und Teams).</div>}
        {b && !!b.teams.length && !teams.length && <div className="klein leise">Keine Kolonne passt zur Auswahl.</div>}
        {b && !!teams.length && q && !sichtbar.length && <div className="hinweis warnung">Suche „{suche}": in diesen 12 Wochen kein Termin.</div>}
        {b && !!teams.length && sichtbar.map((woche) => {
          const n = teams.reduce((x: number, tm: any) => x + woche.reduce((y, t) => y + ((b.tage.find((z: any) => z.team_id === tm.id && z.tag === t)?.termine ?? []).filter(passt).length), 0), 0);
          return (
            <div key={woche[0]} className="wochenblock">
              <div className="wochenkopf">
                <span className="kwmarke">KW {kwVon(woche[0]!)}</span>
                <b>{kurzTag(woche[0]!)} bis {kurzTag(woche[woche.length - 1]!)}{jahrVon(woche[woche.length - 1]!)}</b>
                <div className="wachsen" />
                <span className="klein leise">{n === 1 ? '1 Termin' : `${n} Termine`}</span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="ne4-woche">
                  <thead><tr><th style={{ width: 170 }}>Kolonne</th>{woche.map((t) => (
                    <th key={t} className={t === heute ? 'heute' : undefined}><button type="button" className="alslink" onClick={() => zumTag(t)} title="Tagesplan öffnen">{wochentagKurz(t)} {kurzTag(t)}</button>{feiertag(t) && <div className="klein" style={{ color: 'var(--ueberfaellig, #a33)' }}>{feiertag(t)}</div>}</th>))}</tr></thead>
                  <tbody>{teams.map((tm: any) => (
                    <tr key={tm.id}>
                      <td><b>{tm.name}</b>{firmen.length > 1 && <div className="klein leise">{tm.firma}</div>}
                        {leute(tm).map((p) => <div key={p} className="klein">{p}</div>)}</td>
                      {woche.map((t) => {
                        const x = b.tage.find((z: any) => z.team_id === tm.id && z.tag === t);
                        const termine: any[] = (x?.termine ?? []).filter(passt);
                        const urlaub = x?.verfuegbarkeit === 'urlaub';
                        const klasse = (x?.gesperrt || (feiertag(t) && !termine.length) ? 'gesperrt' : termine.length ? 'belegt' : 'frei') + (t === heute ? ' heute' : '');
                        return (
                          <td key={t} className={klasse}>
                            {x?.gesperrt && <div className="klein">{urlaub ? (x.grund ?? 'Urlaub') : `gesperrt${x.grund ? `: ${x.grund}` : ''}`}</div>}
                            {termine.map((te: any, i: number) => (
                              <span key={i} className="eintrag ne4-baustelle" role="button" tabIndex={0} onClick={() => oeffne(te.auftrag_id)} onKeyDown={(e) => { if (e.key === 'Enter') oeffne(te.auftrag_id); }} title={`Baustelle öffnen, KLS ${te.kls_id ?? ''}${te.name ? `, ${te.name}` : ''}`}>
                                <b>{te.art === 'GEB' ? `${te.strasse ?? ''} ${te.hausnummer ?? ''}` : `${String(te.beginn ?? '').slice(0, 5)} ${te.strasse ?? ''} ${te.hausnummer ?? ''}`}</b>
                                <span className="leise"> {te.ort ?? ''}{te.block_tage > 1 ? `, Tag ${te.block_tag}/${te.block_tage}` : ''}{te.zustand === 'ausgefuehrt' ? ', fertig' : ''}</span>
                              </span>
                            ))}
                            {!x?.gesperrt && !termine.length && <span className="klein leise">{feiertag(t) ? 'Feiertag' : x?.verfuegbarkeit === 'frei' ? 'verfügbar' : 'frei'}</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

/** Eine Verzögerung melden: der Termin bleibt, die Dispo erfährt Grund und bis wann. */
export function VerzoegerungDialog({ termin, titel, zu, fertig }: { termin: any; titel: string; zu: () => void; fertig: () => void }) {
  const [grund, setGrund] = useState('');
  const [bis, setBis] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  async function senden() {
    setLaeuft(true); setFehler(null);
    try { await api.ne4Verzoegerung(termin.id, grund, bis || null); fertig(); } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }
  return (
    <Dialog titel="Verzögerung melden" untertitel={titel} breite={520} laeuft={laeuft} zu={zu}>
      <Fehlerkasten lage={fehler} />
      <Text name="grund" titel="Was verzögert sich und warum?" wert={grund} setze={setGrund} fehler={fehler} mehrzeilig platzhalter="z. B. Keller verschlossen, Hausmeister erst morgen da" />
      <div className="feld"><label>Voraussichtlich fertig am (wenn bekannt)</label><input type="date" value={bis} onChange={(e) => setBis(e.target.value)} style={{ width: 180 }} /></div>
      <p className="klein leise">Der Termin bleibt bestehen. Die Dispo sieht die Meldung sofort an ihrer Glocke und in der Auftragsinfo.</p>
      <div className="reihe" style={{ marginTop: 12 }}>
        <button type="button" className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
        <button type="button" className="knopf haupt" onClick={senden} disabled={laeuft || !grund.trim()}>Melden</button>
      </div>
    </Dialog>
  );
}

/**
 * FERTIG MELDEN UND NICHT ERLEDIGT (.595).
 *
 * ORIGINALTEXT MARVIN (02.11.2026)
 *   „fertig melden, ich muss ja auch eine Selektion machen, wenn ich nicht alle
 *    Wohneinheiten gebaut habe und auch da vielleicht noch eine Info dazu
 *    schreiben ... Es muss ein Kommentar noch dann da eventuell zu der Wohnung
 *    gesteckt werden ... Der sollte dann eigentlich prüfen, nö, sind alle
 *    Dateien hochgeladen ... Was habe ich gebaut? Wie viele Etagen? ... Und
 *    Fotonachweise brauchen wir da auch nicht, weil die sollen die auf der
 *    anderen Seite rüberziehen."
 *
 * Je Connect: gebaut ja oder nein, die Wohnung oder ein Kommentar, beim
 * Nichtbauen der Grund. Dazu die gebauten Etagen und ein Bericht. Die Dateien
 * liegen in den Ordnern; fehlt ein Pflichtordner, geht das Haus nicht auf
 * fertig. Bei „Nicht erledigt" dasselbe, als Teilstand, mit „neuer Tag nötig".
 */
export function MeldeDialog({ termin, art, positionen, zu, fertig, fehlendePflicht = [] }: { termin: any; art: 'fertig' | 'nicht'; positionen: any[]; zu: () => void; fertig: () => void; fehlendePflicht?: string[] }) {
  const passend = positionen.filter((p) => p.art === termin.art || p.art === 'beide');
  const [gewaehlt, setGewaehlt] = useState<Record<string, number>>(Object.fromEntries(passend.slice(0, 1).map((p) => [p.id, 1])));
  const [bericht, setBericht] = useState('');
  const [istMin, setIstMin] = useState('');
  const [etagen, setEtagen] = useState('');
  const [grund, setGrund] = useState(GRUENDE[0]!);
  const [grundText, setGrundText] = useState('');
  const [neuerTag, setNeuerTag] = useState(true);
  const wohnungen: any[] = Array.isArray(termin.wohnungen) ? termin.wohnungen : [];
  const [bau, setBau] = useState<Record<string, { erledigt: boolean; lage: string; grund: string }>>(() =>
    Object.fromEntries(wohnungen.map((w) => [w.id, { erledigt: art === 'fertig' ? true : !!w.erledigt_am, lage: w.lage ?? '', grund: w.grund ?? '' }])));
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const setze = (id: string, x: Partial<{ erledigt: boolean; lage: string; grund: string }>) => setBau((alt) => ({ ...alt, [id]: { ...alt[id]!, ...x } }));
  const gebaut = Object.values(bau).filter((x) => x.erledigt).length;
  const ohneGrund = art === 'fertig' ? wohnungen.filter((w) => !bau[w.id]?.erledigt && !bau[w.id]?.grund.trim()) : [];
  const gesperrt = art === 'fertig' && fehlendePflicht.length > 0;
  async function senden() {
    setLaeuft(true); setFehler(null);
    try {
      const liste = wohnungen.map((w) => ({ id: w.id, erledigt: !!bau[w.id]?.erledigt, lage: bau[w.id]?.lage ?? '', grund: bau[w.id]?.erledigt ? null : (bau[w.id]?.grund || null) }));
      const et = etagen.trim() === '' ? null : Number(etagen.replace(',', '.'));
      if (art === 'fertig') {
        await api.ne4Fertig(termin.id, { bericht: bericht || null, etagen: et, wohnungen: liste, ist_min: termin.block_tag && istMin ? Math.round(Number(String(istMin).replace(',', '.')) * 60) : null, positionen: Object.entries(gewaehlt).filter(([, m]) => m > 0).map(([position_id, menge]) => ({ position_id, menge })) });
      } else {
        const g = grund === 'Anderes' ? (grundText || 'Anderes') : grund + (grundText ? `: ${grundText}` : '');
        await api.ne4NichtErledigt(termin.id, g, { etagen: et, wohnungen: liste, neuer_tag: neuerTag, bericht: bericht || null });
      }
      fertig();
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }
  return (
    <Dialog titel={art === 'fertig' ? 'Fertig melden' : 'Nicht erledigt (Teilstand)'} untertitel={`${termin.strasse ?? ''} ${termin.hausnummer ?? ''}${termin.block_tag ? `, Tag ${termin.block_tag} von ${termin.block_tage}` : ''}`} breite={760} laeuft={laeuft} zu={zu}>
      <Fehlerkasten lage={fehler} />
      {gesperrt && <div className="hinweis warnung"><b>Erst die Dateien:</b> {fehlendePflicht.join(', ')} {fehlendePflicht.length === 1 ? 'ist' : 'sind'} noch leer. Fotos und Nachweise im Haus unter „Dateien" in den Ordner ziehen, dann fertig melden.</div>}
      {art === 'nicht' && (
        <>
          <div className="feld"><label>Grund</label>
            <select value={grund} onChange={(e) => setGrund(e.target.value)}>{GRUENDE.map((g) => <option key={g}>{g}</option>)}</select></div>
          <Text name="grundText" titel="Genauer" wert={grundText} setze={setGrundText} fehler={fehler} />
          <label className="wahlzeile" style={{ gap: 6 }}><input type="checkbox" checked={neuerTag} onChange={(e) => setNeuerTag(e.target.checked)} /> Es braucht einen neuen Tag</label>
        </>
      )}
      {!!wohnungen.length && (
        <div className="feld">
          <label>Was ist gebaut? ({gebaut} von {wohnungen.length} Connects)</label>
          <div className="reihe" style={{ gap: 6, marginBottom: 6 }}>
            <button type="button" className="knopf klein" onClick={() => setBau((alt) => Object.fromEntries(Object.entries(alt).map(([k, v]) => [k, { ...v, erledigt: true }])))}>alle gebaut</button>
            <button type="button" className="knopf klein" onClick={() => setBau((alt) => Object.fromEntries(Object.entries(alt).map(([k, v]) => [k, { ...v, erledigt: false }])))}>keiner</button>
          </div>
          <table className="liste"><tbody>
            {wohnungen.map((w) => (
              <tr key={w.id} style={{ cursor: 'default' }}>
                <td style={{ width: 120 }}><label style={{ display: 'flex', gap: 6, alignItems: 'center', fontWeight: 600 }}>
                  <input type="checkbox" checked={!!bau[w.id]?.erledigt} onChange={(e) => setze(w.id, { erledigt: e.target.checked })} />{w.bezeichnung}</label></td>
                <td><input value={bau[w.id]?.lage ?? ''} placeholder="Wohnung, Bewohner, Kommentar" onChange={(e) => setze(w.id, { lage: e.target.value })} style={{ width: '100%' }} /></td>
                <td style={{ width: 220 }}>{!bau[w.id]?.erledigt && <input value={bau[w.id]?.grund ?? ''} placeholder="Warum nicht gebaut?" onChange={(e) => setze(w.id, { grund: e.target.value })} style={{ width: '100%' }} />}</td>
              </tr>
            ))}
          </tbody></table>
          {ohneGrund.length > 0 && <div className="klein" style={{ color: 'var(--ueberfaellig, #a33)' }}>Ohne Haken und ohne Grund: {ohneGrund.map((w) => w.bezeichnung).join(', ')}. Das Haus bleibt dann offen.</div>}
        </div>
      )}
      <div className="reihe" style={{ gap: 12, flexWrap: 'wrap' }}>
        {termin.art === 'GEB' && <Text name="etagen" titel={`Gebaute Etagen${termin.etagen ? ` (von ${termin.etagen})` : ''}`} wert={etagen} setze={setEtagen} fehler={fehler} breite={140} platzhalter="z. B. 3" />}
        {termin.block_tag && art === 'fertig' && <Text name="ist_min" titel="Gearbeitete Stunden heute" wert={istMin} setze={setIstMin} fehler={fehler} breite={160} platzhalter="z. B. 7,5" />}
      </div>
      {art === 'fertig' && !!passend.length && (
        <div className="feld"><label>Positionen</label>
          {passend.map((p) => (
            <label key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '4px 0', fontWeight: 400 }}>
              <input type="checkbox" checked={(gewaehlt[p.id] ?? 0) > 0} onChange={(e) => setGewaehlt({ ...gewaehlt, [p.id]: e.target.checked ? 1 : 0 })} />
              <span><b>{p.nummer}</b> {p.text}</span>
              {(gewaehlt[p.id] ?? 0) > 0 && p.einheit !== 'Stück' && <input type="number" min={0} step="0.5" value={gewaehlt[p.id]} style={{ width: 70 }} onChange={(e) => setGewaehlt({ ...gewaehlt, [p.id]: Number(e.target.value) })} />}
            </label>
          ))}
        </div>
      )}
      <Text name="bericht" titel={art === 'fertig' ? 'Info an die Dispo (kurz)' : 'Was ist noch zu tun?'} wert={bericht} setze={setBericht} fehler={fehler} mehrzeilig platzhalter={art === 'fertig' ? 'z. B. Sammelpunkt im Keller gesetzt, Eigentümer hat abgenommen' : 'z. B. 2. OG fehlt, Wohnung 5 nicht angetroffen'} />
      <p className="klein leise">Fotos und Nachweise kommen nicht hier hinein, sondern im Haus unter „Dateien" in die Ordner.</p>
      <div className="reihe" style={{ marginTop: 12 }}>
        <button type="button" className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
        <button type="button" className="knopf haupt" onClick={senden} disabled={laeuft || gesperrt}>{art === 'fertig' ? 'Fertig melden' : 'Teilstand melden'}</button>
      </div>
    </Dialog>
  );
}
